import {
    getUrlParams,
    getWindowObject,
    HAS_WINDOW,
} from '@eventbrite/feature-detection';
import { createSelector } from 'reselect';
import { CONCIERGE_TARGET_LOCATIONS } from '../../constants';
import { DiscoverState } from '../../types';

const OVERRIDE_KEY = 'nlweb';
const OVERRIDE_VALUE = 'opt-in';

const windowEnableOverride = (_state: DiscoverState) => {
    if (process.env.REACT_APP_TARGET === 'server') {
        return false;
    }

    if (HAS_WINDOW) {
        const search = getUrlParams();
        return search.get(OVERRIDE_KEY) === OVERRIDE_VALUE;
    }

    return false;
};

const isMobileDevice = (_state: DiscoverState) => {
    if (process.env.REACT_APP_TARGET === 'server') {
        return false;
    }

    if (HAS_WINDOW) {
        // Logic borrowed from Statsig Utils for Bucketed Width.
        const innerWidth = getWindowObject('innerWidth');
        if (typeof innerWidth !== 'number') {
            return false;
        }

        const deviceWidth = Math.round(innerWidth / 10) * 10;

        // The values are pulled from EDS Breakpoints
        // with `small` representing mobile and `medium`
        // representing tablet widths.
        // https://eventbrite.atlassian.net/wiki/spaces/FRONTEND/pages/15500640332/EDS+Breakpoints
        if (deviceWidth < 481) {
            return true;
        }
    }

    return false;
};

const isHomepage = (_state: DiscoverState) => {
    if (process.env.REACT_APP_TARGET !== 'server' && HAS_WINDOW) {
        const windowLocation = getWindowObject('location');
        return windowLocation?.pathname == '/';
    }
};

const setSessionTrackingProperty = (
    experienceEnabled: boolean,
    splitAssignment: boolean,
): void => {
    if (process.env.REACT_APP_TARGET !== 'server' && HAS_WINDOW) {
        // Add property to the User Session:
        const heap = (window as any).heap;
        if (heap && heap.addEventProperties) {
            heap.addEventProperties({
                nlwebAssignment: splitAssignment ? 'test' : 'control',
                nlwebEnabled: experienceEnabled,
            });
        }
    }
};

/**
 * Selector for checking if AI search is enabled for the current location
 */
export const selectIsAISearchEnabled = createSelector(
    [
        (state: DiscoverState): boolean =>
            !!state.app?.featureFlags?.enableChatbot,
        (state: DiscoverState) => state.location,
        windowEnableOverride,
        isMobileDevice,
        isHomepage,
    ],
    (
        enableChatbot,
        location,
        enableOverride,
        isMobile,
        isHomepage,
    ): boolean => {
        const placeId = location.placeId || '';
        const slug = location.slug || '';
        const homepageOrOverride: boolean = isHomepage || enableOverride;
        const placeIdOrLocationPrefixMatches =
            !!CONCIERGE_TARGET_LOCATIONS.find((candidate) => {
                return (
                    placeId == candidate.placeId ||
                    slug.startsWith(candidate.locationPrefix)
                );
            });

        const isInTargetGroup =
            isMobile && homepageOrOverride && placeIdOrLocationPrefixMatches;
        const isChatbotEnabled =
            (enableOverride || enableChatbot) && isInTargetGroup;

        if (isInTargetGroup) {
            setSessionTrackingProperty(isChatbotEnabled, enableChatbot);
        }

        return isChatbotEnabled;
    },
);
